import { ReactNode } from 'react'
import { flatMap, pipe, uniqBy } from 'lodash/fp'
import {
  SettingsMember,
  AccessChannel,
  AccessMemberGroup,
  MinimalChannel,
  RecipientsSelectorValueType,
  Workspace,
} from 'types'

export const uniqueRecipients = uniqBy(
  ({ value, type }: UserValue | ChannelValue | EmailValue | CompanyValue | PsuedoGroupValue) =>
    `${type}::${type === 'Email' ? value : value.id}`,
)
export const isUserValue = (value: Value): value is UserValue => value.type === RecipientsSelectorValueType.User
export const isChannelValue = (value: Value): value is ChannelValue =>
  value.type === RecipientsSelectorValueType.Channel
export const isUserOrChannelValue = (value: Value): value is UserValue | ChannelValue =>
  isUserValue(value) || isChannelValue(value)
export const isEmailValue = (value: Value): value is EmailValue => value.type === RecipientsSelectorValueType.Email
export const isAccessMemberGroupValue = (value: Value): value is AccessMemberGroupValue =>
  value.type === RecipientsSelectorValueType.AccessMemberGroup

export type Value = UserValue | ChannelValue | EmailValue | AccessMemberGroupValue | CompanyValue | PsuedoGroupValue

export const areSameRecipientType = <A extends Value>(recipientA: A, recipientB: Value): recipientB is A =>
  recipientA.type === recipientB.type

export const accessGroupsToUserAndChannelMembers = pipe(
  flatMap((member: AccessMemberGroupValue): Array<UserValue | ChannelValue> => {
    const channels: ChannelValue[] = member.value.channels.map((value) => ({
      type: RecipientsSelectorValueType.Channel,
      value,
    }))
    const users: UserValue[] = member.value.members.map((value) => ({ type: RecipientsSelectorValueType.User, value }))
    return [...channels, ...users]
  }),
  uniqueRecipients,
)

export const recipientsAreSame = (recipientA: Value, recipientB: Value) => {
  switch (recipientA.type) {
    case RecipientsSelectorValueType.User: {
      return areSameRecipientType(recipientA, recipientB) && recipientA.value.id === recipientB.value.id
    }
    case RecipientsSelectorValueType.Channel: {
      return areSameRecipientType(recipientA, recipientB) && recipientA.value.id === recipientB.value.id
    }
    case RecipientsSelectorValueType.Email: {
      return areSameRecipientType(recipientA, recipientB) && recipientA.value === recipientB.value
    }
    case RecipientsSelectorValueType.Company: {
      return areSameRecipientType(recipientA, recipientB) && recipientA.value.id === recipientB.value.id
    }
    case RecipientsSelectorValueType.AccessMemberGroup: {
      return areSameRecipientType(recipientA, recipientB) && recipientA.value.name === recipientB.value.name
    }
  }
}

export interface AccessMemberGroupValue {
  type: RecipientsSelectorValueType.AccessMemberGroup
  value: AccessMemberGroup
}

export interface SourceData {
  users: SettingsMember[]
  channels: AccessChannel[]
  accessMemberGroups?: AccessMemberGroup[]
}

export interface UserValue {
  type: RecipientsSelectorValueType.User
  value: SettingsMember
}

export interface EmailValue {
  type: RecipientsSelectorValueType.Email
  value: string
}

export interface CompanyValue {
  type: RecipientsSelectorValueType.Company
  value: Workspace
}

export interface PsuedoGroupValue {
  type: RecipientsSelectorValueType.LayerEditors
  value: GroupInfo
}

export type Suggestion = UserValue | ChannelValue | AccessMemberGroupValue | CompanyValue | PsuedoGroupValue

export interface ChannelValue {
  type: RecipientsSelectorValueType.Channel
  value: MinimalChannel
}

export interface State {
  sourceData: SourceData
  query: string
  matches: Suggestion[]
  highlightedSuggestionIndex: number
  highlightedRecipientIndex: number | 'all'
}

export type Action =
  | { type: 'updateQuery'; query: string }
  | { type: 'updateSourceData'; sourceData: SourceData }
  | { type: 'updateHighlightedSuggestionIndex'; index: number }
  | { type: 'updateHighlightedRecipientIndex'; index: number | 'all' }

export interface RecipientsSelectorProps {
  id?: string
  sourceData: SourceData
  recipients: Value[]
  onChange: (recipients: Value[]) => void
  placeholder?: string
  className?: string
  suggestionsLimit?: number
  limit?: number
  dropdownClassName?: string
  onFocus?: () => void
  onBlur?: () => void
  allowEmails?: boolean
  disabled?: boolean
  onRecipientActionClick?: () => void
  recipientsClassNames?: string
  reviewerRolesDropdown?: ReactNode
  topGroups?: { group: GroupInfo; childRows: GroupInfo[] }[]
}

export interface GroupInfo {
  id?: number
  avatar_url: string
  name: string
  type: RecipientsSelectorValueType
  member_count?: number
}

interface Clickable {
  onMouseEnter: () => void
  onMouseLeave: () => void
  onMouseDown: (e: React.MouseEvent) => void
}

interface Highlightable {
  highlighted: boolean
}

export interface SuggestionProps extends Highlightable {
  avatar: {
    url?: string | null
    label: string
  }
  title: string
  description: string
  groupId?: number
  type: RecipientsSelectorValueType
}
export interface UserSuggestionProps extends Clickable, Highlightable {
  recipient: UserValue['value']
}

export interface ChannelSuggestionProps extends Clickable, Highlightable {
  recipient: ChannelValue['value']
}

export interface AccessMemberGroupSuggestionProps extends Clickable, Highlightable {
  recipient: AccessMemberGroupValue['value']
}

export interface RecipientSuggestionProps extends Clickable, Highlightable {
  recipient: Value
  highlighted: boolean
}
